'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { TTSManager } from './tts/TTSManager'; // Your provider class
import { TTSOptions } from './tts/types';

export function useTTS() {
  const [isReady, setIsReady] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const managerRef = useRef<TTSManager | null>(null);

  // Initialize TTS manager once
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const init = async () => {
      try {
        managerRef.current = new TTSManager();
        await managerRef.current.initialize();
        console.log('debug: TTSManager initialized');
        setIsReady(true);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('TTS init failed'));
        setIsReady(false);
      }
    };

    init();

    return () => {
      managerRef.current?.destroy();
    };
  }, []);

  const speak = useCallback(
    async (options: TTSOptions) => {
      if (!managerRef.current || !isReady) {
        console.log(
          'debug: print these two value:',
          managerRef.current,
          isReady,
        );
        console.warn('TTS not ready');
        return;
      }

      setError(null);
      setIsSpeaking(true);

      console.log('debug: useTTS speak options:', options);
      try {
        await managerRef.current.speak({
          ...options,
          onStart: () => {
            options.onStart?.();
          },
          onEnd: () => {
            setIsSpeaking(false);
            options.onEnd?.();
          },
          onError: (err) => {
            setIsSpeaking(false);
            setError(err);
            options.onError?.(err);
          },
        });
      } catch (err) {
        setIsSpeaking(false);
        setError(err instanceof Error ? err : new Error('Speak failed'));
      }
    },
    [isReady],
  );

  const stop = useCallback(() => {
    managerRef.current?.stop();
    setIsSpeaking(false);
  }, []);

  const pause = useCallback(() => {
    managerRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    managerRef.current?.resume();
  }, []);

  return {
    speak,
    stop,
    pause,
    resume,
    isReady,
    isSpeaking,
    error,
    capabilities: managerRef.current?.getCapabilities(),
  };
}
